// The footer (ASN acknowledge prompt + workflow/batch-edit actions + Close)
// of CallOffDetailModal.tsx, split out to keep CallOffDetailModal.tsx under
// the 500-line cap. No behaviour change from the original inline JSX —
// moved verbatim.
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Save, Plus, AlertCircle, ExternalLink,
} from 'lucide-react';
import type { CallOff, CallOffLinkedASN } from '../../../lib/api';
import { Button } from '../../ui/button';
import CallOffDetailActions from './CallOffDetailActions';
import type { CallOffAction } from './CallOffDetailActions';
import CallOffConfirmLoad from './CallOffConfirmLoad';

interface CallOffDetailModalFooterProps {
  pendingAckAsns: CallOffLinkedASN[];
  onClose: () => void;
  inlineEditMode: boolean;
  saveBatchInlineEdit: () => void;
  savingBatchEdit: boolean;
  changeCount: number;
  hasOverLimit: boolean;
  overLineCount: number;
  cancelInlineEditMode: () => void;
  showAddParts: boolean;
  setShowAddParts: (value: boolean) => void;
  callOff: CallOff | null;
  isCustomer: boolean;
  isSupplier: boolean;
  actionLoading: boolean;
  canEdit: boolean;
  supplierCanEditLines: boolean;
  /** Called when customer wants to open wizard in edit mode */
  onEditInWizard?: (callOff: CallOff) => void;
  onAction: (action: CallOffAction, skipValidation?: boolean, reason?: string) => void;
  onEnterInlineEdit: () => void;
  /** CR-7781: the Haulage block has unsaved edits, so Confirm Load waits. */
  haulageDirty: boolean;
  /** CR-7781: reload after the haulier confirms the load. */
  onLoadConfirmed: () => void;
}

/**
 * Footer for CallOffDetailModal: the ASN acknowledge prompt (CR-6291), then
 * either the supplier's batch inline-edit controls or the workflow action
 * buttons, and the Close button.
 *
 * @param props - ASN acknowledge state, batch-edit state and workflow action handlers
 * @returns the modal's footer
 */
export default function CallOffDetailModalFooter({
  pendingAckAsns, onClose, inlineEditMode, saveBatchInlineEdit, savingBatchEdit,
  changeCount, hasOverLimit, overLineCount, cancelInlineEditMode, showAddParts,
  setShowAddParts, callOff, isCustomer, isSupplier, actionLoading, canEdit,
  supplierCanEditLines, onEditInWizard, onAction, onEnterInlineEdit,
  haulageDirty, onLoadConfirmed,
}: CallOffDetailModalFooterProps) {
  const navigate = useNavigate();

  return (
    <div className="border-t bg-muted/10">
      {/* CR-7732: acknowledging is the customer's, and only the customer's —
          the endpoint refuses anyone else ("Only the customer can acknowledge
          this ASN"). This asked nothing about who was reading, so the supplier
          and the haulier were both told to do something they cannot. */}
      {isCustomer && pendingAckAsns.length > 0 && (
        <div className="px-4 pt-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">Please acknowledge the ASN.</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {pendingAckAsns.map((a) => (
              <Button
                key={a.id}
                size="sm"
                variant="outline"
                className="gap-1 border-amber-300 text-amber-900 hover:bg-amber-100"
                onClick={() => { onClose(); navigate(`/purchasing?tab=asn&asn=${a.id}`); }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View {a.asn_number}
              </Button>
            ))}
          </div>
        </div>
      )}
      <div className="p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Supplier batch edit controls take priority when in edit mode */}
        {inlineEditMode ? (
          <>
            <Button
              size="sm"
              className="gap-1.5 bg-green-600 hover:bg-green-700"
              onClick={saveBatchInlineEdit}
              disabled={savingBatchEdit || changeCount === 0 || hasOverLimit}
              title={hasOverLimit ? 'Reduce over-limit quantities first' : undefined}
            >
              {savingBatchEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Changes
            </Button>
            <Button size="sm" variant="outline" onClick={cancelInlineEditMode} disabled={savingBatchEdit}>
              Cancel Edit
            </Button>
            {changeCount > 0 && (
              <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                {changeCount} change{changeCount !== 1 ? 's' : ''}
              </span>
            )}
            {hasOverLimit && (
              <span className="px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
                {overLineCount} item{overLineCount !== 1 ? 's' : ''} exceed available qty
              </span>
            )}
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowAddParts(!showAddParts)}>
              <Plus className="h-3.5 w-3.5" />
              {showAddParts ? 'Hide Add Parts' : 'Add Parts'}
            </Button>
          </>
        ) : callOff && (
          <>
            {/* CR-7781: the haulier's action sits with the other workflow
                actions, whichever tab is open. */}
            <CallOffConfirmLoad
              callOff={callOff}
              haulageDirty={haulageDirty}
              onConfirmed={onLoadConfirmed}
            />
            <CallOffDetailActions
              callOff={callOff}
              isCustomer={isCustomer}
              isSupplier={isSupplier}
              actionLoading={actionLoading}
              canEdit={canEdit}
              supplierCanEditLines={supplierCanEditLines}
              canEditInWizard={Boolean(onEditInWizard)}
              onAction={onAction}
              onEditInWizard={() => { onClose(); onEditInWizard?.(callOff); }}
              onEnterInlineEdit={onEnterInlineEdit}
              onClose={onClose}
            />
          </>
        )}
      </div>
      <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted font-medium">
        Close
      </button>
      </div>
    </div>
  );
}
